import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useAppContext from "../../../../context/useAppContext";
import { CodeBlockFileDownloadInfo } from "./types";
import { APILogResponseBodyPayload, LogType } from "../../../IntegrationsManagementEntities";
import LogSidePanelLoading from "./LogSidePanelLoading";
import { LogSidePanelBody } from "./body/LogSidePanelBody";
import { LogSidePanelNotFound } from "./error-views/LogSidePanelNotFound";
import { LogSidePanelUnauthorized } from "./error-views/LogSidePanelUnauthorized";
import { LogSidePanelBanner } from "./banner/LogSidePanelBanner";
import { Divider, Drawer } from "@merge-api/merge-javascript-shared";
import { UserType } from "../../../../../models/Entities";

type Props = {
  title: string;
  logID?: string;
  fetchLog: (props: {
    logID: string;
    onSuccess: (data: LogType) => void;
    onError: (error: Response | undefined) => void;
  }) => void;
  fetchResponseBody?: (props: {
    logID: string;
    onSuccess: (data: APILogResponseBodyPayload) => void;
  }) => void;
};

export const LogSidePanel = ({ title, logID, fetchLog, fetchResponseBody }: Props) => {
  const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const [logEntry, setLogEntry] = useState<undefined | LogType>();
  const [logResponseBody, setLogResponseBody] = useState<undefined | string>();
  const [logDoesNotExist, setLogDoesNotExist] = useState<boolean>(false);
  const [isRedacted, setIsRedacted] = useState<boolean>(false);
  const [logResponseBodyDownloadInfo, setLogResponseBodyDownloadInfo] = useState<
    CodeBlockFileDownloadInfo | undefined
  >();

  const history = useHistory();
  const location = useLocation();
  const { user } = useAppContext();
  const privilegedRoles = new Set([
    UserType.admin_with_billing,
    UserType.admin_without_billing,
    UserType.support,
  ]);
  const showPrivillaged = privilegedRoles.has(user.type);

  useEffect(() => {
    if (!logID) return;

    let isCancelled = false;
    setLogEntry(undefined);
    setLogResponseBody(undefined);
    setLogResponseBodyDownloadInfo(undefined);
    setLogDoesNotExist(false);

    const fetchWithRetry = async (retryCount = 10) => {
      if (retryCount === 0) {
        if (!isCancelled) {
          setLogDoesNotExist(true);
        }
        return;
      }

      fetchLog({
        logID: logID,
        onSuccess: (data) => {
          if (!isCancelled) {
            setLogEntry(data);
            setIsRedacted(data.is_redacted ?? false);
          }
        },
        onError: async (error) => {
          if (error?.status === 404) {
            await sleep(2500);
            fetchWithRetry(retryCount - 1);
          } else {
            if (!isCancelled) {
              setLogDoesNotExist(true);
            }
          }
        },
      });
    };

    fetchWithRetry();

    return () => {
      isCancelled = true;
    };
  }, [fetchLog, logID]);

  useEffect(() => {
    if (!logEntry || !logID) return;

    if (fetchResponseBody === undefined) {
      setLogResponseBody(logEntry.response_body);
      return;
    }

    let isCancelled = false;

    fetchResponseBody({
      logID,
      onSuccess: (data: APILogResponseBodyPayload) => {
        if (isCancelled) return;
        setLogResponseBody(data.response_body);
        if (data.download_link !== undefined && data.file_size != undefined) {
          // Response Body is link, not Object
          const previewMessage = `Download full response as file (${(
            data.file_size / 1000000
          ).toFixed(1)} MB)`;
          setLogResponseBodyDownloadInfo({
            downloadLink: data.download_link,
            previewMessage: previewMessage,
          });
        }
      },
    });

    return () => {
      isCancelled = true;
    };
  }, [fetchResponseBody, logEntry, logID]);

  const onClickExit = () => {
    if (!logID) return;

    if (history.location.state === undefined) {
      const lastSlashIndex = location.pathname.lastIndexOf("/");
      const baseUrl = location.pathname.substring(0, lastSlashIndex);
      history.push(baseUrl);
    } else {
      history.goBack();
    }
  };

  return (
    <Drawer open={logID !== undefined} onClose={onClickExit}>
      {logEntry ? (
        showPrivillaged ? (
          <>
            <LogSidePanelBanner title={title} logEntry={logEntry} onClickExit={onClickExit} />
            <Divider />
            <LogSidePanelBody
              logEntry={logEntry}
              hasDynamicResponseBody={fetchResponseBody !== undefined}
              responseBody={logResponseBody}
              downloadInfo={logResponseBodyDownloadInfo}
              isRedacted={isRedacted}
            />
          </>
        ) : (
          <>
            <LogSidePanelBanner title={title} logEntry={logEntry} onClickExit={onClickExit} />
            <Divider />
            <LogSidePanelUnauthorized />
          </>
        )
      ) : logDoesNotExist ? (
        <LogSidePanelNotFound onClickExit={onClickExit} />
      ) : (
        <LogSidePanelLoading title={title} onClickExit={onClickExit} />
      )}
    </Drawer>
  );
};
